import React, { Fragment, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Drawer } from '../Shell/Shell'
import { Spinner } from '../Loaders/Loaders'
import Portal from 'components/Portal/Portal'
import { Modal } from 'components/Modal/Modal'
import ActionButton, { ActionButtonGroup } from 'components/ActionButton/ActionButton'
import RequestRecordAccess from '../RequestRecordAccess/RequestRecordAccess'
import MiscellaneousAmendmentModal from './MiscellaneousAmendmentModal'
import MiscellaneousOrderRequestModal from './MiscellaneousOrderRequestModal'
import MiscellaneousPublicHistoryModal from './MiscellaneousPublicHistoryModal'
import DocumentEditIcon from '@tecuity/sos-icons/react/document-edit'
import CertificateIcon from '@tecuity/sos-icons/react/certificate'
import HistoryIcon from '@tecuity/sos-icons/react/history'
import UserGroupIcon from '@tecuity/sos-icons/react/user-group'
import { setSuccessToast } from '../../actions'
import { FilingSourceTypeIds } from '../../Utilities/Utilities'
import * as api from '../../requests'
import '../DetailsList/DetailsList.scss'

const MiscellaneousRecordDrawer = ({ show, record, columns, onDismiss, drawerData, drawerLoading }) => {
  const dispatch = useDispatch()
  const [requestAccessModalOpen, setRequestAccessModalOpen] = useState(false)
  const [amendmentModalOpen, setAmendmentModalOpen] = useState(false)
  const [availableAmendments, setAvailableAmendments] = useState([])
  const [amendmentsLoading, setAmendmentsLoading] = useState(false)
  const [orderModalOpen, setOrderModalOpen] = useState(false)
  const [availableOrderFilings, setAvailableOrderFilings] = useState([])
  const [orderFilingsLoading, setOrderFilingsLoading] = useState(false)
  const [historyModalOpen, setHistoryModalOpen] = useState(false)
  const firstCol = (columns || [])[0]
  let title = ''
  if (record && firstCol) {
    if (firstCol.Key === 'RecordNum') {
      title = record.RecordNum || ''
    } else if (firstCol.Key === 'RegistrationDate') {
      title = record.RegistrationDate || ''
    } else {
      title = (record.Fields && record.Fields[firstCol.Key]) || ''
    }
  } else if (record) {
    title = record.RecordNum || ''
  }

  const details = (drawerData && drawerData.Details) || []
  const canFileAmendments = !!(drawerData && drawerData.CanFileAmendments)
  const canFileOrderRequests = !!(drawerData && drawerData.CanFileOrderRequests)
  const canViewHistory = !!(drawerData && drawerData.CanViewHistory)
  const canRequestAccess = !!(drawerData && drawerData.CanRequestAccess)
  const canManageAccess = !!(drawerData && drawerData.CanManageAccess)
  const hasTopActions = canFileAmendments || canFileOrderRequests
  const hasBottomActions = canViewHistory || canRequestAccess || canManageAccess

  return (
    <Drawer
      showCloseButton
      show={!!show}
      title={title}
      onSidebarDismiss={onDismiss}
    >
      {drawerLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '16px' }}>
          <Spinner size="medium" themeColor="secondary" />
        </div>
      )}
      {!drawerLoading && drawerData && (
        <Fragment>
          {hasTopActions && (
            <ActionButtonGroup className="details-actions-top">
              {canFileAmendments && (
                <ActionButton
                  label="File Amendment"
                  onClick={() => {
                    setAmendmentModalOpen(true)
                    setAmendmentsLoading(true)
                    api.GET_MISC_RECORD_AMENDMENTS(
                      record && record.RecordId,
                      (data) => {
                        setAvailableAmendments(data || [])
                        setAmendmentsLoading(false)
                      },
                      () => {
                        setAvailableAmendments([])
                        setAmendmentsLoading(false)
                      }
                    )
                  }}
                >
                  <DocumentEditIcon />
                </ActionButton>
              )}
              {canFileOrderRequests && (
                <ActionButton
                  label="Request Documents"
                  onClick={() => {
                    setOrderModalOpen(true)
                    setOrderFilingsLoading(true)
                    api.GET_MISC_RECORD_ORDER_FILINGS(
                      record && record.RecordId,
                      (data) => {
                        setAvailableOrderFilings(data || [])
                        setOrderFilingsLoading(false)
                      },
                      () => {
                        setAvailableOrderFilings([])
                        setOrderFilingsLoading(false)
                      }
                    )
                  }}
                >
                  <CertificateIcon />
                </ActionButton>
              )}
            </ActionButtonGroup>
          )}
          {details.length > 0 && (
            <Fragment>
              <hr className="details-divider" />
              <table className="details-list container-fluid">
                <tbody>
                  {details.map((detail, i) => (
                    <tr key={i} className="detail">
                      <td className="label">{detail.Label}</td>
                      <td className="value">{detail.Value || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <hr className="details-divider" />
            </Fragment>
          )}
          {hasBottomActions && (
            <ActionButtonGroup className="details-actions-bottom">
              {canViewHistory && (
                <ActionButton
                  label="View History"
                  onClick={() => setHistoryModalOpen(true)}
                >
                  <HistoryIcon />
                </ActionButton>
              )}
              {canRequestAccess && (
                <ActionButton
                  label="Request Access"
                  // onClick={() => setRequestAccessModalOpen(true)}
                  onClick={() => dispatch(setSuccessToast("This is where you will request access when it's implemented :)"))}
                >
                  <UserGroupIcon />
                </ActionButton>
              )}
              {canManageAccess && (
                <ActionButton
                  label="Manage Access"
                  onClick={() => dispatch(setSuccessToast("This is where you will manage access when it's implemented :)"))}
                >
                  <UserGroupIcon />
                </ActionButton>
              )}
            </ActionButtonGroup>
          )}
        </Fragment>
      )}
      <Portal isOpened={requestAccessModalOpen}>
        <Modal
          title={`Request Access - ${title}`}
          closeModal={() => setRequestAccessModalOpen(false)}
          modalSize="medium"
          autoHeight
          closeButton
        >
          <RequestRecordAccess
            sourceId={record && record.RecordId}
            sourceTypeId={FilingSourceTypeIds.Miscellaneous}
          />
        </Modal>
      </Portal>
      <MiscellaneousAmendmentModal
        show={amendmentModalOpen}
        onDismiss={() => setAmendmentModalOpen(false)}
        amendments={availableAmendments}
        loading={amendmentsLoading}
        recordNum={record && record.RecordNum}
      />
      <MiscellaneousOrderRequestModal
        show={orderModalOpen}
        onDismiss={() => setOrderModalOpen(false)}
        orderFilings={availableOrderFilings}
        loading={orderFilingsLoading}
        recordId={record && record.RecordId}
      />
      <MiscellaneousPublicHistoryModal
        show={historyModalOpen}
        onDismiss={() => setHistoryModalOpen(false)}
        recordNum={record && record.RecordNum}
      />
    </Drawer>
  )
}

export default MiscellaneousRecordDrawer
